import {computed, inject, Injectable, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ComposerStateService} from '@app/model-composer';
import {CompositionSelectionService} from '@app/model-composer/services/composition-selection.service';
import {OrderService} from '@service/order/order.service';
import {MatDialog} from '@angular/material/dialog';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {catchError, filter, map, startWith, switchMap, tap} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {AddToOrderComponent} from '../../../components/add-to-order/add-to-order.component';
import {CompositionCustomizationService} from '@app/model-composer/services/composition-customization.service';
import {VestaStateService} from '@app/vesta/services/vesta-state.service';
import {ScreenshotData} from '@app/vesta/models/vesta-screenshot.model';

type PricingSummaryElement = {
  id: number | string;
  placements: string[];
  quantity: number;
  uid?: string;
  code?: string;
  image?: string;
  dimensions?: string;
  name?: string;
};

type SummaryData = {
  configurations: PricingSummaryElement[];
  amountAll?: string;
  confIds?: number[];
};

export type PricingState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; value: SummaryData }
  | { kind: 'error'; message: string };

type PricingElementPayload = { id: string; placements: string[]; quantity: number; uid?: string; code?: string; image?: string; dimensions?: string; name?: string };

type PricingPayload = {
  elements: PricingElementPayload[];
  customerId: any;
};

@Injectable({ providedIn: 'root' })
export class ComposerPricingService {
  private http = inject(HttpClient);
  private state = inject(ComposerStateService);
  private selection = inject(CompositionSelectionService);
  private customization = inject(CompositionCustomizationService);
  private vesta = inject(VestaStateService);
  private order = inject(OrderService);
  private dialog = inject(MatDialog);
  private requestedKey = signal<string | null>(null);
  private draftPayload = computed<PricingPayload | null>(() => {
    const composition = this.state.composition();
    const oldCompositionId = this.state.oldCompositionId();
    if (!composition.length) { return null; }
    const codeMap = this.selection.placementCodeMaterialColorMap();
    const map = new Map<string, PricingElementPayload>();
    for (const item of composition) {
      const elementId = item.element.id;
      const placements = !item?.element?.originalPlacements
        ? item.element.activePlacements
        : item.element.placements;
      const finalPlacements = placements.map(plc => {
        const code = codeMap.get(plc);
        return code ? `${plc}_${code}` : plc;
      });
      const key = `${elementId}::${[...finalPlacements].sort().join('|')}`;
      const existing = map.get(key);
      if (existing) {
        existing.quantity++;
      } else { map.set(key, { id: elementId, placements: finalPlacements, quantity: 1, uid: item?.uid, code: item?.element?.code, dimensions: item?.element?.dimensions, image: item?.element?.image, name: item?.element?.name }); }
    }
    return {
      activeGroupId: this.customization.activeGroupId() ?? null,
      elements: Array.from(map.values()),
      customerId: this.order.currentClient?.id,
      oldCompositionId: oldCompositionId ? Number(oldCompositionId) : null,
      screenshot3d: this.screenshot3d()
    };
  });

  private _extractCustomizedCOnfiguration(): any {
    return {
      composition: this.state.composition(),
      overrides: this.selection.overrides(),
      elementOverrides: this.selection.elementOverrides(),
      rowMeta: this.selection.rowMeta(),
      revision: this.selection.revision()
    };
  }

  private draftKey = computed(() => {
    const p = this.draftPayload();
    if (!p) { return null; }
    const parts = p.elements
      .map(e => `${e.id}@${e.quantity}:${e.placements.join('|')}`)
      .sort();
    return `${p.customerId}|${parts.join(';')}`;
  });
  readonly stale = computed(() => {
    const dk = this.draftKey();
    const rk = this.requestedKey();
    if (!dk) { return false; }
    if (!rk) { return true; }
    return dk !== rk;
  });
  readonly canOpenSummary = computed(() => {
    const state = this.pricing();
    return state.kind === 'ready' && !this.stale();
  });
  private requestedPayload = computed(() => {
    if (this.draftKey() !== this.requestedKey()) { return null; }
    return this.draftPayload();
  });
  private currentScreenshot = signal<ScreenshotData>({ base64: null, url: null });

  private screenshot3d = computed(() => {
    const screenshot = this.currentScreenshot();
    return screenshot?.base64 ?? screenshot?.url ?? null;
  });

  private pricing$ = toObservable(this.requestedPayload).pipe(
    switchMap(payload => {
      if (!payload) { return of<PricingState>({ kind: 'idle' }); }

      return this.http
        .post<any>(`${environment.api.get()}/modular_configuration/summary`, payload)
        .pipe(
          map((res: any) => {
            const data = res?.result?.data as SummaryData;

            if (!data?.configurations) {
              return { kind: 'error', message: 'Risposta pricing non valida' };
            }

            return { kind: 'ready', value: data };
          }),
          startWith({ kind: 'loading' }),
          catchError(() =>
            of({ kind: 'error', message: 'Errore nel calcolo prezzo' })
          )
        );
    })
  );

  readonly pricing: any = toSignal(this.pricing$, {initialValue: { kind: 'idle' }});

  request(): void {
    const dk = this.draftKey();
    if (!dk) {
      return;
    }
    this.requestedKey.set(null);
    this.takeScreenshot$().subscribe((screenshot) => {
      this.currentScreenshot.set(screenshot);
      this.requestedKey.set(dk);
    });
  }

  reset(): void {
    this.requestedKey.set(null);
  }

  openAddToOrder(): void {
    const state = this.pricing();

    if (state.kind !== 'ready') {
      return;
    }

    const summary = state.value;
    const confIds = summary.configurationIds ?? [];

    if (!confIds.length) {
      return;
    }

    const dialogRef = this.openAddToOrderDialog(summary, this.currentScreenshot());

    dialogRef.componentInstance.orderSelected.pipe(
      filter(({ shouldAddToProforma }: { shouldAddToProforma: boolean }) => shouldAddToProforma),
      switchMap(({ createNewOrder }: { createNewOrder: boolean }) =>
        this.addMultipleToOrder(confIds, createNewOrder).pipe(
          tap(() => dialogRef.close())
        )
      )
    ).subscribe();
  }

  private addMultipleToOrder(confIds: number[], createNew: boolean): Observable<any> {
    const existingOrder = this.order.currentOrder;

    if (!createNew && existingOrder?.id) {
      return this.order.orderLineCreate({
        customerId: this.order.currentClient?.id,
        configurationId: confIds,
        customizedConfiguration: this._extractCustomizedCOnfiguration(),
        orderId: existingOrder.id,
        oldCompositionId: this.state.oldCompositionId() ? Number(this.state.oldCompositionId()) : null
      });
    }

    return this.order.orderCreate({
      customerId: this.order.currentClient?.id,
      configurationId: confIds,
      customizedConfiguration: this._extractCustomizedCOnfiguration()
    }).pipe(
      tap(order => {
        this.order.order = order;
      })
    );
  }

  private getVestaScreenshotSize(): { width: number; height: number } {
    return {
      width: 1200,
      height: 900
    };
  }

  private openAddToOrderDialog(summary: any, screenshot: ScreenshotData): any {
    const dialogRef = this.dialog.open(AddToOrderComponent, {
      height: '92vh',
      width: '70vw'
    });

    const confIds = summary.configurationIds ?? [];

    dialogRef.componentInstance.product = {
      productName:
        summary.configurations.length === 1
          ? summary.configurations[0]?.name
          : `Configurazione (${summary.configurations.length} elementi)`,
      totalPrice: summary.amountAll ?? '—',
      configurations: summary.configurations,
      showDownloadImageButton: false,
      screenshot
    };

    dialogRef.componentInstance.configurationId = confIds;
    dialogRef.componentInstance.confScreenShot = screenshot?.base64 ?? screenshot?.url ?? null;

    return dialogRef;
  }

  private takeScreenshot$(): Observable<ScreenshotData> {
    const screenshotFn = this.vesta.screenshotFn();

    if (!screenshotFn) {
      return of({ base64: null, url: null });
    }

    return new Observable<ScreenshotData>((observer) => {
      try {
        screenshotFn(
          {
            camera: 'main',
            dimension: this.getVestaScreenshotSize()
          },
          (result) => {
            observer.next({
              base64: result?.base64 ?? null,
              url: result?.url ?? null
            });
            observer.complete();
          }
        );
      } catch (error) {
        console.error('[ComposerPricingService] screenshot error', error);
        observer.next({ base64: null, url: null });
        observer.complete();
      }
    });
  }
}
